import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import invariant from "tiny-invariant";
import { type QueryOptions } from "../types";
import {
  type Address,
  erc20Abi,
  formatUnits,
  getContract,
  type PublicClient,
} from "viem";
import {
  useAccount,
  useChainId,
  usePublicClient,
  useWatchBlockNumber,
} from "wagmi";

import { formatCurrency } from "../web3";

type Result = {
  formatted: string;
  balance: bigint;
};

type Currency = {
  address: Address;
  decimals: number;
};

type Params = {
  accountAddress: Address | undefined;
  currency: Currency | undefined;
  pc: PublicClient | undefined;
};

export const getBalanceQueryKey = ({
  accountAddress,
  currency,
}: Omit<Params, "pc">) => {
  return ["use-balance-query", accountAddress, currency];
};

const getQueryOptions = <TData = Result>(
  params: Params,
  options?: QueryOptions<Result, unknown, TData>
) => {
  const { accountAddress, currency, pc } = params;

  return queryOptions<Result, unknown, TData>({
    queryKey: getBalanceQueryKey({
      accountAddress,
      currency,
    }),
    queryFn: async () => {
      invariant(currency, "useBalanceQuery. currency is undefined");
      invariant(pc, "useBalanceQuery. pc is undefined");
      invariant(accountAddress, "useBalanceQuery. address is undefined");

      const contract = getContract({
        abi: erc20Abi,
        address: currency.address,
        client: pc,
      });

      const raw = await contract.read.balanceOf([accountAddress]);
      const fmt = formatCurrency(formatUnits(raw, currency.decimals));
      return {
        formatted: fmt || formatUnits(raw, currency.decimals),
        balance: raw,
      };
    },
    staleTime: Infinity,
    enabled: Boolean(accountAddress && pc && currency),
    ...options,
  });
};

type UseBalancesQueries = {
  currencies: Params["currency"][];
};

export const useBalancesQueries = <TData = Result>(
  params: UseBalancesQueries,
  options?: QueryOptions<Result, unknown, TData>
) => {
  const { address } = useAccount();
  const pc = usePublicClient();
  return useQueries({
    queries: params.currencies.map((currency) => {
      return getQueryOptions(
        { currency, accountAddress: address, pc },
        options
      );
    }),
  });
};

type UseBalanceQuery = {
  currency: Params["currency"];
};
export const useBalanceQuery = <TData = Result>(
  params: UseBalanceQuery,
  options?: QueryOptions<Result, unknown, TData>
) => {
  const { address } = useAccount();
  const chainId = useChainId();
  const pc = usePublicClient({ chainId });

  const preparedParams = {
    accountAddress: address,
    currency: params.currency
      ? {
          address: params.currency.address,
          decimals: params.currency.decimals,
        }
      : undefined,
  };

  const query = useQuery(
    getQueryOptions(
      {
        ...preparedParams,
        pc,
      },
      options
    )
  );

  useWatchBlockNumber({
    chainId,
    pollingInterval: 4000,
    onBlockNumber: () => {
      if (query.isPending || query.isError) return;

      query.refetch();
    },
  });

  return query;
};
